/**
 * Transfers API (TMMAPP-0026 / TMMAPI-0054) — classify / clear a line, list strict candidates,
 * link / unlink a managed pair. Wire types from `src/api/generated/`; normalized domain out.
 * A line is either a transfer or allocated — never both (server wall).
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  OwnerRegisterLine as WireOwnerRegisterLine,
  TransferCandidate as WireTransferCandidate,
  TransferCandidatesResponse as WireCandidatesResponse,
  TransferLinkResponse as WireTransferLinkResponse,
  ClaimTransferLinesRequest,
  ClaimTransferLinesResponse as WireClaimResponse,
} from './generated';
import { normalizeOwnerRegisterLine, type OwnerRegisterLine } from './transactions';

export interface TransferCandidate {
  transactionId: number;
  accountId: number;
  accountName: string;
  payee: string;
  postedOn: string;
  operatingAmountMinor: number;
  currency: string;
  counterpartyTransactionId: number;
  counterpartyAccountId: number;
  counterpartyAccountName: string;
  counterpartyPostedOn: string;
  counterpartyOperatingAmountMinor: number;
}

export interface TransferLinkResult {
  transactionIdA: number;
  transactionIdB: number;
  currencyA: string | null;
  currencyB: string | null;
  differenceMinor: number;
}

export function normalizeTransferCandidate(w: WireTransferCandidate): TransferCandidate {
  return {
    transactionId: w.transactionId,
    accountId: w.accountId,
    accountName: w.accountName,
    payee: w.payee,
    postedOn: w.postedOn,
    operatingAmountMinor: w.operatingAmountMinor,
    currency: w.currency,
    counterpartyTransactionId: w.counterpartyTransactionId,
    counterpartyAccountId: w.counterpartyAccountId,
    counterpartyAccountName: w.counterpartyAccountName,
    counterpartyPostedOn: w.counterpartyPostedOn,
    counterpartyOperatingAmountMinor: w.counterpartyOperatingAmountMinor,
  };
}

export function normalizeTransferCandidates(w: WireCandidatesResponse): TransferCandidate[] {
  return w.items.map(normalizeTransferCandidate);
}

export function normalizeTransferLink(w: WireTransferLinkResponse): TransferLinkResult {
  return {
    transactionIdA: w.transactionIdA,
    transactionIdB: w.transactionIdB,
    currencyA: w.currencyA,
    currencyB: w.currencyB,
    differenceMinor: w.differenceMinor,
  };
}

/** What `claim-transfer-lines` did (TMMAPI-0157): lines it couldn't classify are listed, never forced. */
export interface ClaimTransferLinesResult {
  classified: number;
  rulesCreated: number;
  skipped: { transactionId: number; reason: string }[];
}

export function normalizeClaimTransferLines(w: WireClaimResponse): ClaimTransferLinesResult {
  return {
    classified: w.classified,
    rulesCreated: w.rulesCreated,
    skipped: w.skipped.map((row) => ({ transactionId: row.transactionId, reason: row.reason })),
  };
}

function requiredLine(wire: WireOwnerRegisterLine): OwnerRegisterLine {
  const line = normalizeOwnerRegisterLine(wire);
  if (!line) throw new ApiShapeError('transaction.id is required');
  return line;
}

export const transfersApi = {
  /** POST /v1/me/accounts/{accountId}/transactions/{txId}/transfer */
  async classify(
    accountId: number,
    txId: number,
    transferAccountId: number,
  ): Promise<OwnerRegisterLine> {
    return requiredLine(
      await request<WireOwnerRegisterLine>(
        'POST',
        `/v1/me/accounts/${accountId}/transactions/${txId}/transfer`,
        { json: { transferAccountId } },
      ),
    );
  },
  /** DELETE /v1/me/accounts/{accountId}/transactions/{txId}/transfer */
  async clear(accountId: number, txId: number): Promise<OwnerRegisterLine> {
    return requiredLine(
      await request<WireOwnerRegisterLine>(
        'DELETE',
        `/v1/me/accounts/${accountId}/transactions/${txId}/transfer`,
      ),
    );
  },
  /** GET /v1/me/transfers/candidates?accountId&days */
  async candidates(accountId?: number, days?: number): Promise<TransferCandidate[]> {
    return normalizeTransferCandidates(
      await request<WireCandidatesResponse>('GET', '/v1/me/transfers/candidates', {
        query: {
          accountId,
          days,
        },
      }),
    );
  },
  /** POST /v1/me/transfers/link */
  async link(transactionIdA: number, transactionIdB: number): Promise<TransferLinkResult> {
    return normalizeTransferLink(
      await request<WireTransferLinkResponse>('POST', '/v1/me/transfers/link', {
        json: { transactionIdA, transactionIdB },
      }),
    );
  },
  /** DELETE /v1/me/transfers/link/{txId} */
  async unlink(txId: number): Promise<void> {
    await request<void>('DELETE', `/v1/me/transfers/link/${txId}`);
  },
  /**
   * POST /v1/me/accounts/{accountId}/claim-transfer-lines (TMMAPI-0157): the lines on the owner's
   * other accounts that name `mask` become transfers to this account; idempotent.
   */
  async claimTransferLines(
    accountId: number,
    mask: string,
    createRules: boolean,
  ): Promise<ClaimTransferLinesResult> {
    return normalizeClaimTransferLines(
      await request<WireClaimResponse>(
        'POST',
        `/v1/me/accounts/${accountId}/claim-transfer-lines`,
        {
          json: { mask, createRules },
        },
      ),
    );
  },
  /**
   * The same claim by a `MISSING_ACCOUNT` item's identity key (TMMAPI-0170): a wire's `ACCT:` /
   * `NAME:` key or a Zelle line's `ZELLE:` key, sent as served. The account remembers the
   * identity: the item leaves the queue and future lines are classified at ingestion.
   */
  async claimIdentity(
    accountId: number,
    identityKey: string,
    createRules: boolean,
  ): Promise<ClaimTransferLinesResult> {
    return normalizeClaimTransferLines(
      await request<WireClaimResponse>(
        'POST',
        `/v1/me/accounts/${accountId}/claim-transfer-lines`,
        { json: { identityKey, createRules } satisfies ClaimTransferLinesRequest },
      ),
    );
  },
};
